import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDetailsFromFormFields,
  fetchDropdowns,
  showMessageBox,
} from "../../fn";
import MultiSelect from "../../../ui/multiselect"; // Import MultiSelect
import "./form.css";

function Form({
  formFields,
  submitApi,
  formHeading,
  validationRules = [],
  calculationRules = [],
  data = null,
  redirectPath = null,
  redirectOnCreateOnly = true,
  onBeforeSubmit = null,
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("zTaskAuthToken");
  const [formDetails, setFormDetails] = useState({
    fields: [],
    defaultValues: {},
  }); // Initialize formDetails based on what getDetailsFromFormFields returns
  const [dropdowns, setDropdowns] = useState({}); // Initialize dropdowns as an empty object
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({
    fields: [],
    error: "",
  });
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Effect to initialize form details and default values
  useEffect(() => {
    const details = getDetailsFromFormFields(formFields); //
    setFormDetails(details);
    setFormData(data || details.defaultValues);
  }, [formFields, data]);

  useEffect(() => {
    const loadDropdowns = async () => {
      if (Object.keys(dropdowns).length === 0 && !isDropdownLoading) {
        setIsDropdownLoading(true);
        try {
          await fetchDropdowns(formFields, formData, setDropdowns);
        } catch (error) {
          console.error("Error fetching dropdown data:", error);
          showMessageBox(
            "Failed to load form data. Please try again.",
            "error"
          );
        } finally {
          setIsDropdownLoading(false);
        }
      }
    };

    loadDropdowns();
  }, [formFields, formData, dropdowns, isDropdownLoading]);

  const handleChange = async (e, multiSelectValues = undefined) => {
    const { name, value, type, checked } = e?.target || {};

    let newFormData = { ...formData };
    let fieldName = name;
    let fieldValue = value;

    if (multiSelectValues !== undefined) {
      // This is for MultiSelect
      fieldName = e; // e is the fieldName for MultiSelect
      fieldValue = multiSelectValues;
    } else if (type === "checkbox") {
      fieldValue = checked;
    } else {
      fieldValue = value.length === 0 ? null : value;
    }

    newFormData[fieldName] = fieldValue;

    for (const rule of calculationRules) {
      if (rule.triggerFields.includes(fieldName)) {
        // Pass the fieldName as the trigger field
        const calculatedValues = await rule.calculate(newFormData, fieldName);
        newFormData = { ...newFormData, ...calculatedValues };
      }
    }

    setFormData(newFormData);
    if (formErrors.fields.includes(fieldName)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        fields: prevErrors.fields.filter((field) => field !== fieldName),
        error: "",
      }));
    }
  };

  const validateForm = (dataToValidate) => {
    let errors = {
      fields: [],
      error: "",
    };
    let isValid = true;

    for (const rule of validationRules) {
      const validationResult = rule.validator(dataToValidate);

      if (validationResult) {
        isValid = false;
        errors.fields = validationResult.fields;
        errors.error = validationResult.message;
        break;
      }
    }
    return { isValid, errors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({ fields: [], error: "" });
    setIsLoading(true);

    const { isValid, errors } = validateForm(formData);

    if (!isValid) {
      setFormErrors(errors);
      showMessageBox(
        errors.error || "Please correct the highlighted fields.",
        "error"
      );
      setIsLoading(false);
      return;
    }

    if (onBeforeSubmit) {
      try {
        const canProceed = await onBeforeSubmit(formData);
        if (!canProceed) {
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error("Pre-submit validation failed:", error);
        showMessageBox("Unable to validate form. Please try again.", "error");
        setIsLoading(false);
        return;
      }
    }

    const method = data ? "PUT" : "POST";
    const apiUrl = data ? `${submitApi}/${data.id}` : submitApi;

    const payload = { ...formData };
    for (const fieldName in payload) {
      const field = formFields.find((f) => f.fieldName == fieldName);
      if (field == undefined) continue;
      if (field.isMulti) {
        payload[fieldName] = payload[fieldName].map((e) => {
          console.log(e);
          return field.send_value ? e.label : e.value;
        });
      }
    }

    try {
      const response = await fetch(apiUrl, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        console.log("Form submitted successfully:", payload);
        showMessageBox(
          `Form ${data ? "updated" : "submitted"} successfully!`,
          "success"
        );
        // Redirect to list after create and update when path is provided
        if (redirectPath && (redirectOnCreateOnly ? !data : true)) {
          navigate(redirectPath);
        }
      } else {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        const errorText =
          errorData.error ||
          errorData.message ||
          "An unexpected error occurred.";

        if (errorData.fields && Array.isArray(errorData.fields)) {
          setFormErrors({
            fields: errorData.fields,
            error: errorText,
          });
        }
        showMessageBox(errorText, "error");
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
      showMessageBox(
        "Failed to connect to the server. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header-row">
        {redirectPath && (
          <button
            type="button"
            className="backbutton form-back-btn"
            onClick={() => navigate(redirectPath)}
            aria-label="Back to list"
          >
            ← Back
          </button>
        )}
        <h2 className="form-title">
          {data ? "Update" : "Add New"} {formHeading}
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="form">
        {formDetails.fields.map((row, rowIndex) => (
          <div className="form-row" key={rowIndex}>
            {row.map((item) => (
              <FormField
                key={item.fieldName}
                item={item}
                dropdowns={dropdowns}
                formErrors={formErrors}
                formData={formData}
                handleChange={handleChange}
                isDropdownLoading={isDropdownLoading}
              />
            ))}
          </div>
        ))}

        <div className="form-actions">
          {redirectPath && (
            <button
              type="button"
              className="backbutton"
              onClick={() => navigate(redirectPath)}
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading
              ? data
                ? "Updating..."
                : "Submitting..."
              : data
              ? "Update"
              : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FormField({
  item,
  dropdowns,
  formErrors,
  formData,
  handleChange,
  isDropdownLoading,
}) {
  const isCheckbox = item.tag === "input" && item.inputType === "checkbox";
  const hasError = formErrors.fields.includes(item.fieldName);

  const formatDateTimeForInput = (dateValue, type) => {
    if (!dateValue) return "";
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      // Try to parse string to Date object
      try {
        date = new Date(dateValue);
        if (isNaN(date.getTime())) return ""; // Not a valid date string
      } catch {
        return ""; // Handle cases where new Date(dateValue) might throw an error
      }
    }

    if (type === "date") {
      return date.toISOString().split("T")[0]; //
    }
    if (type === "datetime-local") {
      //
      return date.toISOString().slice(0, 16);
    }
    return "";
  };

  let selectedMultiSelectValues = [];
  if (item.tag === "select" && item.isMulti) {
    const currentValues = formData[item.fieldName];
    const options = dropdowns[item.fieldName];

    if (Array.isArray(currentValues) && options) {
      for (const val of currentValues) {
        const option = options.find((opt) => {
          // Check if the stored value matches option.id or option.value/label
          if (typeof val === "object" && val !== null) {
            // If the stored value is an object (e.g., from initial load of an existing record)
            return opt.id === val.value || opt.value === val.label;
          } else {
            // If the stored value is a primitive (string/number)
            return item.send_value ? opt.value === val : opt.id === val;
          }
        });
        if (option) {
          selectedMultiSelectValues.push({
            value: option.id,
            label: option.value,
          });
        }
      }
    } else if (currentValues && !Array.isArray(currentValues) && options) {
      // Handle case where a single value might be stored but it's a multi-select field
      const option = options.find((opt) =>
        item.send_value ? opt.value === currentValues : opt.id === currentValues
      );
      if (option) {
        selectedMultiSelectValues.push({
          value: option.id,
          label: option.value,
        });
      }
    }
  }

  return (
    <div
      className={`form-group ${isCheckbox ? "checkbox-group" : ""} ${
        hasError ? "has-error" : ""
      }`}
    >
      {isCheckbox ? (
        <>
          <input
            type="checkbox"
            id={item.fieldName}
            name={item.fieldName}
            checked={formData[item.fieldName] || false}
            onChange={handleChange}
            {...(item.props || {})}
          />
          <label htmlFor={item.fieldName}>
            {item.displayName}
            {item.required && <span className="required">*</span>}
          </label>
        </>
      ) : (
        <label htmlFor={item.fieldName}>
          {item.displayName}:
          {item.required && <span className="required">*</span>}
        </label>
      )}

      {item.tag === "input" && !isCheckbox && (
        <input
          type={item.inputType}
          id={item.fieldName}
          name={item.fieldName}
          value={
            item.inputType === "date" || item.inputType === "datetime-local"
              ? formatDateTimeForInput(formData[item.fieldName], item.inputType)
              : formData[item.fieldName] || ""
          }
          onChange={handleChange}
          required={item.required}
          maxLength={item.maxLength}
          {...(item.props || {})}
        />
      )}

      {item.tag === "select" && item.isMulti ? (
        <MultiSelect
          options={
            dropdowns[item.fieldName]?.map((option) => ({
              value: option.id,
              label: option.value,
            })) || []
          }
          selectedValues={selectedMultiSelectValues}
          onSelect={(selectedOpts) =>
            handleChange(item.fieldName, selectedOpts)
          }
          placeholder={`Select ${item.displayName}`}
          disabled={item.readOnly || isDropdownLoading}
        />
      ) : (
        item.tag === "select" && (
          <select
            name={item.fieldName}
            id={item.fieldName}
            value={formData[item.fieldName] || ""}
            onChange={handleChange}
            required={item.required}
            {...(item.props || {})}
          >
            <option value="">
              Select {item.displayName}
              {item.required ? "" : " (Optional)"}
            </option>
            {dropdowns[item.fieldName]?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.value}
              </option>
            ))}
          </select>
        )
      )}

      {item.tag === "textarea" && (
        <textarea
          id={item.fieldName}
          name={item.fieldName}
          value={formData[item.fieldName] || ""}
          onChange={handleChange}
          required={item.required}
          maxLength={item.maxLength}
          {...(item.props || {})}
        ></textarea>
      )}
      {hasError && <p className="error-message">{formErrors.error}</p>}
    </div>
  );
}

export default Form;
